/**
 * Backend base URL + data flag.
 *
 * The Flask backend (Lambda + API Gateway) is deployed at
 * `https://api.screenatlas.app` and serves `/health`, `/v1/...`, `/auth/...` at
 * the root — there is no `/api` prefix and no local/dev URL.
 *
 * `USE_MOCK_DATA = true` serves all screen data from `mock.ts` instead.
 */

/** Set to `true` to serve all screen data from `mock.ts`. */
export const USE_MOCK_DATA = false;

/**
 * Environment overrides. Defaults are production. Expo inlines
 * `process.env.EXPO_PUBLIC_*` at build time, so a local dev build can target
 * the dev environment with e.g.
 *   EXPO_PUBLIC_API_BASE_URL=https://apidev.screenatlas.app
 *   EXPO_PUBLIC_IMAGES_BASE_URL=https://d23jx02qze5mb6.cloudfront.net
 * The web app maps its `VITE_*` values onto the same names in vite.config.ts
 * so shared mappers agree with `web/src/api/config.ts`.
 */
const fromEnv = (value: string | undefined, fallback: string): string =>
  value && value.trim() ? value.trim().replace(/\/+$/, '') : fallback;

/** Backend origin (no trailing slash, no `/api` prefix). */
export const API_BASE_URL = fromEnv(
  process.env.EXPO_PUBLIC_API_BASE_URL,
  'https://api.screenatlas.app',
);

/** True when the build targets anything but the production API (dev/local). */
export const IS_DEV_ENV = API_BASE_URL !== 'https://api.screenatlas.app';

/**
 * Image delivery. Artwork is mirrored from TMDB into the private S3 image
 * bucket by `backend/scripts/sync_images.py` and served through CloudFront
 * (storage stack `CloudFrontHostname` output). Items carry `*_key` fields
 * once synced; mappers prefer those and fall back to the TMDB CDN for items
 * the sync hasn't reached yet.
 */
export const IMAGES_BASE_URL = fromEnv(
  process.env.EXPO_PUBLIC_IMAGES_BASE_URL,
  'https://dazlx7yiuf9js.cloudfront.net',
);

/** TMDB image CDN — fallback for items not synced to S3 yet. */
export const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p';
export type TmdbImageSize = 'w92' | 'w154' | 'w185' | 'w300' | 'w342' | 'w500' | 'w780' | 'original';
